<template>
  <v-select
    :value="currentValue"
    :items="selectItems"
    item-value="id"
    return-object
    dense
    outlined
    hide-details
    class="basket-item__promotion-select"
    :class="{ 'hide-append-icon': currentValue }"
    :error="error"
    :label="label"
    @input="$emit('input', $event)">
    <template #selection="{ item }">
      <div v-if="item" class="basket-item__promotion-selection">
        <img
          v-if="item.image"
          :src="item.image"
          alt=""
          class="basket-item__promotion-selection-image" />
        <span class="basket-item__promotion-selection-name">
          {{ item.name }}
          {{ item.isCoins ? group.quantity : `${item.price} ${item.currency}` }}
          <CoinIcon v-if="item.isCoins" />
        </span>
      </div>
      <span v-else class="basket-item__promotion-placeholder">
        {{ label }}
      </span>
    </template>
    <template #item="{ item }">
      <div class="basket-item__promotion-item">
        <img
          v-if="item.image"
          :src="item.image"
          :alt="item.name"
          class="basket-item__promotion-selection-image" />
        <span class="basket-item__promotion-selection-label">
          <span class="basket-item__promotion-selection-name font-weight-light">
            {{ item.name }}
          </span>
          <span
            class="ml-3 font-weight-medium"
            :class="$vuetify.theme.dark ? 'white--text' : 'text--black'">
            {{
              item.isCoins ? group.quantity : `${item.price} ${item.currency}`
            }}
            <CoinIcon v-if="item.isCoins" />
          </span>
        </span>
      </div>
    </template>
  </v-select>
</template>

<script>
import { COIN_TYPES } from '@/constants/coins'
import CoinIcon from '@/components/CoinIcon.vue'
import { formatPrice } from '@/utils/price'

export default {
  name: 'PromotionElementSelect',
  components: {
    CoinIcon,
  },
  props: {
    group: { type: Object, required: true },
    variants: { type: Array, default: () => [] },
    selectedGroupVariants: { type: Object, default: () => ({}) },
    selectionIndex: { type: Number, default: 0 },
    label: { type: String, default: '' },
    error: { type: Boolean, default: false },
  },
  computed: {
    currentValue() {
      const selections = this.selectedGroupVariants?.[this.group?.id]
      const selectionsArray = Array.isArray(selections)
        ? selections
        : [selections]
      return selectionsArray[this.selectionIndex] || ''
    },
    groupVariants() {
      if (!this.group?.variants?.length) return []
      return this.group.variants.reduce((acc, groupVariant) => {
        const resolved = this.variants.find(
          (it) => it.id === groupVariant.arena_variant_id,
        )
        if (!resolved) return acc
        if (resolved.track_stock && resolved.stock?.available <= 0) return acc
        acc.push(resolved)
        return acc
      }, [])
    },
    selectItems() {
      return this.groupVariants.map((it) => ({
        id: it.id,
        name: it.name,
        image: it.image,
        price: formatPrice(it.prices[0]?.gross_price),
        currency: it.prices[0]?.currency.symbol || it.prices[0]?.currency.code,
        isCoins: it.attributes?.type === COIN_TYPES.coins,
      }))
    },
    singleCoinsSelectionItem() {
      if (this.groupVariants.length !== 1) return null
      const variant = this.groupVariants[0]
      if (variant?.attributes?.type !== COIN_TYPES.coins) return null
      return this.selectItems[0] || null
    },
  },
  watch: {
    singleCoinsSelectionItem: {
      handler(item) {
        if (!item || this.currentValue) return
        this.$emit('input', item)
      },
      immediate: true,
    },
  },
}
</script>

<style scoped lang="scss">
.basket-item__promotion-select {
  --promotion-select-bg: #fff;
  --promotion-select-text: #2b2b2b;
  background: var(--promotion-select-bg);
  border-radius: 10px;
  margin-bottom: 0;
  width: 100%;

  &.v-input--is-dirty ::v-deep input {
    display: none;
  }

  &.hide-append-icon :deep(.v-input__append-inner) {
    display: none;
  }
}

.theme--dark .basket-item__promotion-select {
  --promotion-select-bg: var(--v-background-base, #1e1e1e);
  --promotion-select-text: rgba(255, 255, 255, 0.87);
}

.theme--dark .basket-item__promotion-item,
.theme--dark .basket-item__promotion-selection-label {
  color: rgba(255, 255, 255, 0.87);
}

.basket-item__promotion-select :deep(.v-input__slot) {
  padding: 0 0.5rem !important;

  .v-label {
    font-size: 0.875rem;
  }
}

.basket-item__promotion-select :deep(.v-text-field__details),
.basket-item__promotion-select :deep(.v-messages) {
  display: none;
  min-height: 0;
}

.basket-item__promotion-selection {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  overflow: hidden;
}

.basket-item__promotion-placeholder {
  color: #d74a4a;
  font-weight: 600;
}

.basket-item__promotion-selection-image {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: cover;
  border-radius: 6px;
}

.basket-item__promotion-selection-label {
  font-size: 0.875rem;
  color: var(--promotion-select-text);
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.basket-item__promotion-selection-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11rem;
}

.basket-item__promotion-item {
  display: flex;
  font-size: 0.875rem;
  color: var(--promotion-select-text);
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.basket-item__promotion-selection-name :deep(.coin-icon),
.basket-item__promotion-selection-label :deep(.coin-icon) {
  display: inline-block;
  vertical-align: middle;
  margin-top: -6px;
}
</style>
